import React, { Component, ReactNode } from 'react';
import { supabase } from '../../lib/supabaseClient';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error info in state
    this.setState({
      error,
      errorInfo,
    });

    // Check if this is an auth-related error
    const isAuthError =
      error.message.includes('auth') ||
      error.message.includes('session') ||
      error.message.includes('token') ||
      error.message.includes('JWT');

    if (isAuthError) {
      console.warn('Auth error detected, clearing potentially corrupted session');
      // Clear potentially corrupted session
      supabase.auth.signOut().catch(e => {
        console.error('Error signing out after auth error:', e);
      });
    }
  }

  handleReload = () => {
    // Clear site data and reload
    supabase.auth.signOut().then(() => {
      window.location.reload();
    }).catch(() => {
      window.location.reload();
    });
  };

  handleContinue = () => {
    // Try to recover by resetting error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <div style={{
            maxWidth: '600px',
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}>
            <h1 style={{
              color: '#d32f2f',
              marginTop: 0,
              marginBottom: '20px',
              fontSize: '24px'
            }}>
              Something went wrong
            </h1>

            <p style={{
              color: '#666',
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              The application encountered an error. This might be due to:
            </p>

            <ul style={{
              color: '#666',
              marginBottom: '30px',
              lineHeight: '1.6'
            }}>
              <li>An expired or corrupted session</li>
              <li>Network connectivity issues</li>
              <li>Temporary data inconsistency</li>
            </ul>

            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '30px'
            }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Sign Out & Reload
              </button>

              <button
                onClick={this.handleContinue}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
              >
                Try to Continue
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: '500', marginBottom: '10px' }}>
                  Error Details (Development Only)
                </summary>
                <div style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#d32f2f',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  {this.state.errorInfo && (
                    <>
                      <br /><br />
                      <strong>Component Stack:</strong>
                      <br />
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
